import { makeStyles } from '@material-ui/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  generateSupportBundle,
  listResmgrHosts,
} from 'app/plugins/openstack/components/infrastructure/actions'
import { listClusterHostGroups } from 'app/plugins/openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'app/plugins/openstack/components/infrastructure/clusters/selectors'
import ClusterList from 'app/plugins/openstack/components/infrastructure/hosts/generate-support-bundle-modal/ClusterList'
import HostList from 'app/plugins/openstack/components/infrastructure/hosts/generate-support-bundle-modal/HostList'
import { resmgrHostsSelector } from 'app/plugins/openstack/components/infrastructure/selectors'
import Alert from 'core/components/Alert'
import Info from 'core/components/validatedForm/Info'
import { useSupportBundleProgress } from 'core/context/supportBundleProgressContext'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'

interface GenerateSupportBundleModalProps {
  rows: any[]
  onClose: (success: boolean) => void
  areRowClusters?: boolean
}

const GenerateSupportBundleModal = ({
  rows,
  onClose,
  areRowClusters = true,
}: GenerateSupportBundleModalProps) => {
  const classes = useStyles()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [selectedHostIds, setSelectedHostIds] = useState<Set<string>>(new Set())
  const [selectedClusterNames, setSelectedClusterNames] = useState<Set<string>>(new Set()) // Tracks explicitly selected real clusters to drive host visibility and selection
  const [isUnassignedHostsSelected, setIsUnassignedHostsSelected] = useState<boolean>(false) // Tracks selection of the “Unassigned hosts” group, which is not a real cluster
  const [hostSearchQuery, setHostSearchQuery] = useState<string>('')
  const { bundles, showSupportBundlePopover } = useSupportBundleProgress()
  const hasInitialized = useRef(false) // Guard to avoid re-initializing selections when data updates

  useListAction(listClusterHostGroups)
  useListAction(listResmgrHosts)

  const allClusters = useSelector(clusterHostGroupsSelector)
  const allHosts = useSelector(resmgrHostsSelector)

  const assignedHostIds = useMemo(() => {
    const ids = new Set<string>()
    allClusters?.forEach((cluster) => {
      cluster?.hostlist?.forEach((hostId) => ids.add(hostId))
    })
    return ids
  }, [allClusters])

  const unassignedHosts = useMemo(() => {
    return allHosts?.filter((host) => !assignedHostIds.has(host?.id)) || []
  }, [allHosts, assignedHostIds])

  const clusterByNameMap = useMemo(() => {
    const map = new Map<string, any>()
    allClusters?.forEach((cluster) => {
      if (cluster?.name) {
        map.set(cluster.name, cluster)
      }
    })
    return map
  }, [allClusters]) // Precompute for O(1) lookups with large cluster lists

  const hostByIdMap = useMemo(() => {
    const map = new Map<string, any>()
    allHosts?.forEach((host) => {
      if (host?.id) {
        map.set(host.id, host)
      }
    })
    return map
  }, [allHosts]) // Precompute for O(1) lookups with large host lists

  const hostToClusterMap = useMemo(() => {
    const map = new Map<string, string>()
    allClusters?.forEach((cluster) => {
      cluster?.hostlist?.forEach((hostId) => {
        if (cluster?.name) {
          map.set(hostId, cluster.name)
        }
      })
    })
    return map
  }, [allClusters]) // Reverse lookup to find which cluster a host belongs to

  const visibleClusters = useMemo(() => {
    if (!rows || rows.length === 0) {
      return allClusters || []
    }

    // Filter clusters based on which page the modal was opened from
    if (areRowClusters) {
      const rowClusterNames = new Set(
        rows.map((cluster: any) => cluster?.name || cluster?.id).filter(Boolean),
      )
      return (
        allClusters?.filter(
          (cluster) => rowClusterNames.has(cluster?.name) || rowClusterNames.has(cluster?.id),
        ) || []
      )
    }

    if (!areRowClusters) {
      const rowHostIds = new Set(rows.map((host: any) => host?.id).filter(Boolean))
      return (
        allClusters?.filter((cluster) => {
          if (!cluster?.hostlist || cluster.hostlist.length === 0) {
            return false
          }
          return cluster.hostlist.some((hostId: string) => rowHostIds.has(hostId))
        }) || []
      )
    }

    return allClusters || []
  }, [allClusters, rows, areRowClusters])

  useEffect(() => {
    if (
      hasInitialized.current ||
      !rows ||
      rows.length === 0 ||
      !allClusters ||
      allClusters.length === 0 ||
      !allHosts ||
      allHosts.length === 0
    ) {
      return
    }

    // Auto-select hosts and clusters based on the rows passed from the parent page
    const hostIdsToSelect = new Set<string>()
    const clustersToSelect = new Set<string>()

    if (areRowClusters) {
      rows.forEach((cluster: any) => {
        const clusterName = cluster?.name || cluster?.id
        if (clusterName) {
          clustersToSelect.add(clusterName)
          if (cluster?.hostlist) {
            cluster.hostlist.forEach((hostId: string) => {
              hostIdsToSelect.add(hostId)
            })
          }
        }
      })
    } else {
      rows.forEach((host: any) => {
        if (host?.id) {
          hostIdsToSelect.add(host.id)
          const clusterName = hostToClusterMap.get(host.id)
          if (clusterName) {
            clustersToSelect.add(clusterName)
          } else {
            setIsUnassignedHostsSelected(true)
          }
        }
      })
    }

    if (hostIdsToSelect.size > 0) {
      setSelectedHostIds(hostIdsToSelect)
    }
    if (clustersToSelect.size > 0) {
      setSelectedClusterNames(clustersToSelect)
    }

    hasInitialized.current = true
  }, [rows, allClusters, allHosts, areRowClusters, hostToClusterMap])

  useEffect(() => {
    // Reset state when modal is closed/reopened with different rows
    if (!rows || rows.length === 0) {
      hasInitialized.current = false
      setSelectedHostIds(new Set())
      setSelectedClusterNames(new Set())
      setIsUnassignedHostsSelected(false)
      setHostSearchQuery('')
    }
  }, [rows])

  const visibleHosts = useMemo(() => {
    // Visibility is driven by cluster selection, not host selection
    const visibleHostsMap = new Map<string, any>()

    selectedClusterNames.forEach((clusterName) => {
      const cluster = clusterByNameMap.get(clusterName)
      if (cluster?.hostlist) {
        cluster.hostlist.forEach((hostId) => {
          if (!visibleHostsMap.has(hostId)) {
            const host = hostByIdMap.get(hostId)
            if (host) {
              visibleHostsMap.set(hostId, host)
            }
          }
        })
      }
    })

    if (isUnassignedHostsSelected && unassignedHosts.length > 0) {
      unassignedHosts.forEach((host) => {
        if (!visibleHostsMap.has(host.id)) {
          visibleHostsMap.set(host.id, host)
        }
      })
    }

    return Array.from(visibleHostsMap.values())
  }, [
    clusterByNameMap,
    hostByIdMap,
    selectedClusterNames,
    isUnassignedHostsSelected,
    unassignedHosts,
  ])

  const filteredHosts = useMemo(() => {
    if (!hostSearchQuery.trim()) {
      return visibleHosts
    }
    const query = hostSearchQuery.toLowerCase()
    return visibleHosts.filter(
      (host) =>
        host?.name?.toLowerCase()?.includes(query) || host?.id?.toLowerCase()?.includes(query),
    )
  }, [visibleHosts, hostSearchQuery])

  const handleHostToggle = (hostId: string, checked: boolean) => {
    setSelectedHostIds((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(hostId)
      } else {
        newSet.delete(hostId)
      }
      return newSet
    })
  }

  const handleSubmit = async () => {
    let selectedHostIdsList = Array.from(selectedHostIds)

    if (selectedHostIdsList.length === 0) {
      setError('Please select at least one host or cluster.')
      return
    }
    // Filter out hosts that are already in a support bundle
    if (bundles.some((bundle) => bundle.hostIds?.some((id) => selectedHostIdsList.includes(id)))) {
      selectedHostIdsList = selectedHostIdsList.filter(
        (id) => !bundles.some((bundle) => bundle.hostIds?.includes(id)),
      )
      if (selectedHostIdsList.length === 0) {
        setError('All hosts are already in a support bundle.')
        return
      }
    }

    setSubmitting(true)
    setError(null)

    try {
      const body = {
        hostIds: selectedHostIdsList,
      }

      const res = (await generateSupportBundle({ body })) as {
        bundleId: string
        invalidHostIds?: string[]
        status?: string
        hostCount?: number
        message?: string
        error?: string
      }

      // Handle invalid host IDs
      if (res?.invalidHostIds && res.invalidHostIds.length > 0) {
        const allInvalid = res.invalidHostIds.length === selectedHostIdsList.length

        if (allInvalid) {
          setError(
            `All selected host IDs are invalid: ${res.invalidHostIds
              .map((id) => hostByIdMap.get(id)?.name || id)
              .join(', ')}. Please select valid hosts.`,
          )
          setSubmitting(false)
          return
        } else {
          // Show warning but continue
          setError(
            `The following host IDs are invalid: ${res.invalidHostIds
              .map((id) => hostByIdMap.get(id)?.name || id)
              .join(', ')}. Bundle generation will continue for valid hosts.`,
          )
        }
      }

      const bundleId = res?.bundleId

      if (bundleId) {
        // Only add to context if we have at least one valid host
        const validHostIds = selectedHostIdsList.filter((id) => !res?.invalidHostIds?.includes(id))

        if (validHostIds.length > 0) {
          showSupportBundlePopover({
            bundleId,
            hostIds: validHostIds,
          })
          onClose(true)
        } else {
          setError('No valid hosts selected. Please try again.')
        }
      } else {
        setError('Failed to get bundle ID. Please try again after a few minutes.')
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          'Failed to generate support bundle. Please try again after a few minutes.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalForm
      title={
        <>
          <Text className={classes.title} variant="subtitle1">
            Generate Support Bundle
          </Text>
          <div className={classes.header}>
            <Text variant="body2">
              Selected Hosts:{' '}
              <Text variant="caption1" component="span">
                {selectedHostIds?.size || 0}
              </Text>
            </Text>
          </div>
        </>
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      submitTitle={'Generate Bundle'}
      submitting={submitting}
      open
      className={classes.modal}
    >
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Select Hosts</Text>
          <Text variant="body2">
            Pick entire clusters or specific hosts to include in the diagnostic bundle. The system
            will collect logs from your selection and create a downloadable package for Support.
          </Text>
        </div>
      </Info>

      {error && (
        <Alert
          variant="error"
          title="Error"
          message={error || 'Failed to generate support bundle'}
          className={classes.error}
        />
      )}
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.leftColumn}>
            <ClusterList
              visibleClusters={visibleClusters}
              isUnassignedHostsSelected={isUnassignedHostsSelected}
              selectedClusterNames={selectedClusterNames}
              setIsUnassignedHostsSelected={setIsUnassignedHostsSelected}
              setSelectedClusterNames={setSelectedClusterNames}
              setSelectedHostIds={setSelectedHostIds}
              unassignedHosts={unassignedHosts}
              clusterByNameMap={clusterByNameMap}
            />
          </div>
          <div className={classes.rightColumn}>
            <HostList
              filteredHosts={filteredHosts}
              hostSearchQuery={hostSearchQuery}
              setHostSearchQuery={setHostSearchQuery}
              selectedHostIds={selectedHostIds}
              handleHostToggle={handleHostToggle}
              bundles={bundles}
            />
          </div>
        </div>
      </div>
    </ModalForm>
  )
}

export default GenerateSupportBundleModal

const useStyles = makeStyles((theme) => ({
  modal: {
    '& .modal-body': {
      paddingBottom: 0,
    },
  },
  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  error: {
    marginBottom: theme.spacing(2),
  },
  info: {
    background: theme.palette.grey[50],
    marginBottom: theme.spacing(2),
  },
  infoContent: {
    display: 'grid',
    gap: 8,
  },
  contentContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    height: '100%',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}))
